import { Shipment, TrackingStatus } from '../types/shipment.types';

export const statusColors: Record<string, string> = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
    'in-transit': 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    'at-port': 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    'on-vessel': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    delivered: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400',
    delayed: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    cancelled: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

export const statusLabels: Record<string, string> = {
    pending: 'Pending',
    'in-transit': 'In Transit',
    'at-port': 'At Port',
    'on-vessel': 'On Vessel',
    delivered: 'Delivered',
    delayed: 'Delayed',
    cancelled: 'Cancelled',
};

export const trackingProviderLabels: Record<string, string> = {
    dhl: 'DHL',
    fedex: 'FedEx',
    ups: 'UPS',
    manual: 'Manual',
    'sea-freight': 'Sea Freight',
    other: 'Other',
};

export const isVesselDelivery = (shipment: Shipment) =>
    shipment.rfqId?.metadata?.deliveryRequiredAt === 'vessel';

export const getOrderStatusLabel = (shipment: Shipment) => {
    switch (shipment.status) {
        case 'pending':
            return shipment.inventoryDeducted ? 'Order Confirmed' : 'Pending';
        case 'cancelled':
            return 'Order Cancelled';
        case 'in-transit':
            return 'In Transit';
        case 'at-port':
            return 'Reached Port';
        case 'on-vessel':
            return 'On Vessel';
        case 'delivered':
            if (shipment.actualDelivery) {
                return 'Delivery Confirmed';
            }
            return isVesselDelivery(shipment) ? 'Delivered to Vessel' : 'Delivered to Port';
        case 'delayed':
            return 'Delayed';
        default:
            return statusLabels[shipment.status] || shipment.status;
    }
};

export const getHistoryStatusLabel = (shipment: Shipment, history: TrackingStatus) => {
    if (history.status === 'delivered') {
        const note = (history.notes || '').toLowerCase();
        if (note.includes('confirmed')) {
            return 'Delivery Confirmed';
        }
        return isVesselDelivery(shipment) ? 'Delivered to Vessel' : 'Delivered to Port';
    }
    if (history.status === 'pending') {
        const note = (history.notes || '').toLowerCase();
        return note.includes('confirmed') ? 'Order Confirmed' : 'Pending';
    }
    if (history.status === 'at-port') {
        return 'Reached Port';
    }
    return statusLabels[history.status] || history.status;
};

export const getHistoryLocationLabel = (shipment: Shipment, history: TrackingStatus) => {
    const locationText = history.location || shipment.currentLocation || '';
    const deliveryTarget = isVesselDelivery(shipment)
        ? shipment.vesselId?.name || shipment.rfqId?.metadata?.vesselDelivery?.vesselName || 'vessel'
        : shipment.destinationPort || 'destination';
    if (history.status === 'pending') {
        const note = (history.notes || '').toLowerCase();
        if (!note.includes('confirmed')) {
            return 'Awaiting vendor confirmation';
        }
        if (shipment.supplyPort) {
            return `Confirmed at ${shipment.supplyPort} • Delivery to ${deliveryTarget}`;
        }
        return `Order confirmed • Delivery to ${deliveryTarget}`;
    }
    if (history.status === 'in-transit') {
        return locationText ? `In transit - ${locationText}` : 'In transit (location update pending)';
    }
    if (history.status === 'on-vessel') {
        return isVesselDelivery(shipment)
            ? `On vessel ${shipment.vesselId?.name || shipment.rfqId?.metadata?.vesselDelivery?.vesselName || ''}`.trim()
            : locationText;
    }
    if (history.status === 'at-port') {
        return shipment.destinationPort ? `Reached ${shipment.destinationPort}` : locationText;
    }
    if (history.status === 'delivered') {
        if (isVesselDelivery(shipment)) {
            return `Delivered to ${shipment.vesselId?.name || shipment.rfqId?.metadata?.vesselDelivery?.vesselName || 'vessel'}`;
        }
        return shipment.destinationPort ? `Delivered to ${shipment.destinationPort}` : locationText;
    }
    return locationText;
};

export const formatMoney = (value: number, currency?: string) => {
    const safeValue = Number.isFinite(value) ? value : 0;
    return `${currency || 'USD'} ${safeValue.toFixed(2)}`;
};

export const resolveFileUrl = (url: string) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const apiUrl = (import.meta as any).env?.VITE_API_URL || 'http://localhost:3000';
    return `${apiUrl}/${url.replace(/^\/+/, '')}`;
};
